import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getOffer, updateOfferStatus } from '../services/offerService';
import { useCompanyInfo } from '../../../hooks/useCompanyInfo';
import { toast } from 'react-toastify';
import {
  Box, Button, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle,
  Menu, MenuItem, ListItemIcon, ListItemText, Stepper, Step, StepLabel, StepConnector,
  stepConnectorClasses, alpha, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TransformIcon from '@mui/icons-material/Transform';
import DraftsIcon from '@mui/icons-material/Drafts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DiscountIcon from '@mui/icons-material/Discount';
import PreviewIcon from '@mui/icons-material/Preview';
import PrintIcon from '@mui/icons-material/Print';
import { useTranslation } from "react-i18next";
import { OFFER_STATUS, normalizeStatus, getStatusColor, getStatusLabel, canConvertOffer, canEditOffer } from '../../../constants/offerConstants';
import InvoicePreviewDialog from '../../../components/InvoicePreviewDialog';

// ✅ Custom Stepper Connector with dynamic colors
const ColorlibConnector = styled(StepConnector)(({ theme, ownerState }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4caf50 0%, #388e3c 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// ✅ Custom Step Icon
const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #4caf50 0%, #388e3c 100%)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  const icons = {
    1: <DraftsIcon />,
    2: <SendIcon />,
    3: <CheckCircleIcon />,
    4: <TransformIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}


const OfferDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const language = useSelector((state) => state.settings?.language) || i18n.language || "nl";
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  
  // Preview state
  const [templateHtml, setTemplateHtml] = useState('');
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // ✅ Get company info from Redux store (auto-fetches if needed)
  const { companyInfo } = useCompanyInfo();

  // ✅ Define fetchData with useCallback to prevent infinite loops
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch offer
      const response = await getOffer(id);
      
      // ✅ Handle different response structures
      let offerData = null;
      if (response?.payload) {
        offerData = response.payload;
      } else if (response?.data) {
        offerData = response.data;
      } else {
        offerData = response;
      }


      // ✅ Validate that we have actual offer data
      if (!offerData || !offerData.Id) {
        throw new Error('Invalid offer data received');
      }

      const lineItems = offerData.LineItems || [];

      // ✅ Base amount (excl. VAT)
      const baseTotalExcl = lineItems.reduce(
        (sum, item) =>
          sum + (item.TotalExcl ?? (Number(item.Quantity || 0) * Number(item.UnitPrice || 0))),
        0
      );

      // ✅ Discount (absolute amount)
      const discountAmount = Number(offerData.Discount || 0);
      const amountAfterDiscount = baseTotalExcl - discountAmount;

      // ✅ VAT percentage
      const vatPercentage = Number(
        offerData.VATPercentage ?? (lineItems[0]?.VATPercent ?? lineItems[0]?.VAT ?? 21)
      );

      // ✅ VAT on discounted amount
      const totalVAT = (amountAfterDiscount * vatPercentage) / 100;

      // ✅ Final total (incl. VAT)
      const totalIncl = amountAfterDiscount + totalVAT;

      // ✅ Paid / due
      const offerPaidAmount = Number(offerData.OfferPaidAmount || 0);
      const offerDueAmount = offerData.OfferDueAmount != null
        ? Number(offerData.OfferDueAmount)
        : totalIncl - offerPaidAmount;

      const mappedOffer = {
        ...offerData,
        Status: normalizeStatus(offerData.Status),
        CustomerName: offerData.Customer?.Name || 'N/A',
        CustomerEmail: offerData.Customer?.Email || 'N/A',
        CustomerVAT: offerData.Customer?.VATNumber || 'N/A',
        CustomerPhone: offerData.Customer?.PhoneNumber || offerData.Customer?.MobilePhone || 'N/A',
        CustomerAddress: `${offerData.Customer?.Street || ''} ${offerData.Customer?.Box || ''}, ${offerData.Customer?.PostCode || ''} ${offerData.Customer?.City || ''}, ${offerData.Customer?.Country || ''}`.trim(),
        LineItems: lineItems.map((item) => ({
          ...item,
          ProductName: item.Product?.Name || `Product #${item.ProductId}`,
          ProductSKU: item.Product?.SKU || item.Product?.Reference || '',
          ProductDescription: item.Product?.Description || '',
          TotalExcl: item.TotalExcl ?? (Number(item.Quantity || 0) * Number(item.UnitPrice || 0)),
        })),
        // ✅ Calculated totals
        TotalExcl: baseTotalExcl,
        DiscountAmount: discountAmount,
        AmountAfterDiscount: amountAfterDiscount,
        VATPercentage: vatPercentage,
        TotalVAT: totalVAT,
        TotalIncl: totalIncl,
        OfferPaidAmount: offerPaidAmount,
        OfferDueAmount: offerDueAmount,
      };

      
      // ✅ Set offer data and stop loading immediately
      // Products are already included in LineItems, no need to fetch all products
      setOffer(mappedOffer);
      setLoading(false);
      

    } catch (error) {
      toast.error('Failed to load offer');
      setLoading(false);
    }
  }, [id]); // Only depends on id

  // Load offer template
  useEffect(() => {
    let file = "offer-template-nl.html";
    if (language === "fr") file = "offer-template-fr.html";
    else if (language === "en") file = "offer-template-en.html";
    
    fetch(`/offertemplates/${file}`)
      .then(res => res.text())
      .then(html => setTemplateHtml(html))
      .catch(() => toast.error('Failed to load offer template'));
  }, [language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ Get product details from offer LineItems (no external fetch needed)
  const getProductDetails = (productId) => {
    if (!productId || !offer?.LineItems) return null;
    const lineItem = offer.LineItems.find(item => item.ProductId === productId);
    return lineItem?.Product || null;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ✅ Get status step index
  const getStatusStep = (status) => {
    switch (status) {
      case OFFER_STATUS.DRAFT: return 0;
      case OFFER_STATUS.SENT: return 1;
      case OFFER_STATUS.ACCEPTED: return 2;
      case OFFER_STATUS.CONVERTED: return 3;
      case OFFER_STATUS.REJECTED: return -1;
      default: return 0;
    }
  };

  // ✅ Get available status transitions
  const getAvailableStatuses = (currentStatus) => {
    const statuses = [];
    if (currentStatus === OFFER_STATUS.CONVERTED) return [];

    if (currentStatus === OFFER_STATUS.DRAFT) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      statuses.push({ value: 'CONVERT', label: 'Convert to Sale Order', icon: <TransformIcon />, color: '#667eea' });
    }
    if (currentStatus === OFFER_STATUS.SENT) {
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
      statuses.push({ value: 'CONVERT', label: 'Convert to Sale Order', icon: <TransformIcon />, color: '#667eea' });
    }
    if (currentStatus === OFFER_STATUS.ACCEPTED) {
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
      statuses.push({ value: 'CONVERT', label: 'Convert to Sale Order', icon: <TransformIcon />, color: '#667eea' });
    }
    if (currentStatus === OFFER_STATUS.REJECTED) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
    }
    return statuses;
  };

  // ✅ Menu handlers
  const handleOpenStatusMenu = (event) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
  };

  // Navigate to Add Sale Order page with offer data pre-populated
  const handleConvert = () => {
    handleCloseStatusMenu();
    navigate('/sales/sale-orders/add', {
      state: {
        fromOffer: {
          customer: offer.Customer,
          lineItems: offer.LineItems,
          discountAmount: offer.DiscountAmount || offer.Discount || 0,
          vatPercentage: offer.VATPercentage,
          customText: offer.CustomText || offer.Notes || '',
          offerId: offer.Id,
          offerNumber: offer.OfferNumber,
        },
      },
    });
  };

  // ✅ Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateOfferStatus(id, newStatus);
      toast.success(`Offer status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update offer status');
    } finally {
      handleCloseStatusMenu();
    }
  };

  // ✅ Handle menu item click
  const handleStatusMenuClick = async (statusValue) => {
    if (statusValue === 'CONVERT') {
      handleConvert();
    } else {
      handleStatusUpdate(statusValue);
    }
  };

  const handleEdit = () => {
    navigate(`/sales/offers/edit/${offer.Id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        toast.success('Offer deleted successfully');
        navigate('/sales/offers');
      } catch (error) {
        toast.error('Failed to delete offer');
      }
    }
  };

  const getFilledTemplate = () => {
    if (!templateHtml || !offer) return "<p>Template not loaded</p>";
    
    const tq = (offer.LineItems || []).reduce((s, p) => s + Number(p.Quantity || 0), 0);
    const DR = (offer.DiscountAmount || offer.Discount || 0) > 0 
      ? `<tr class="discount-row"><td colspan="5" style="text-align:right;font-style:italic;">Discount:</td><td class="text-right">- €${Number(offer.DiscountAmount || offer.Discount || 0).toFixed(2)}</td></tr>` 
      : "";
    
    const PR = (offer.LineItems || []).map((item) => {
      const productName = item.Product?.Name || item.ProductName || 'Unknown Product';
      const n = item.SerialNumber ? `${productName} (SN: ${item.SerialNumber})` : productName;
      const qty = Number(item.Quantity || 0);
      const unitPrice = Number(item.UnitPrice || 0);
      const totalExcl = Number(item.TotalExcl || (qty * unitPrice));
      const vatAmount = Number(item.VATAmount || ((totalExcl * (item.VATPercent || item.VAT || 0)) / 100));
      const totalIncl = totalExcl + vatAmount;
      
      return `<tr><td>${n}</td><td class="text-center">${qty}</td><td class="text-right">€${unitPrice.toFixed(2)}</td><td class="text-right">€${totalExcl.toFixed(2)}</td><td class="text-right">€${vatAmount.toFixed(2)}</td><td class="text-right">€${totalIncl.toFixed(2)}</td></tr>`;
    }).join("");

    const noteText = offer.CustomText || offer.Notes || "";
    const NS = noteText && noteText.trim() 
      ? `<div class="notes-section has-content"><h4>Additional Information</h4><p>${noteText}</p></div>` 
      : "";

    const iban = companyInfo?.iban || "";
    let html = templateHtml
      .replace(/{{CompanyName}}/g, companyInfo?.name || "N/A")
      .replace(/{{CompanyVAT}}/g, companyInfo?.vat || "N/A")
      .replace(/{{CompanyIBAN}}/g, iban || "N/A")
      .replace(/{{CompanyStreetAddress}}/g, companyInfo?.address || "")
      .replace(/{{CompanyCityCountry}}/g, [companyInfo?.postCode, companyInfo?.city, companyInfo?.country].filter(Boolean).join(" ") || "")
      .replace(/{{CustomerName}}/g, offer.Customer?.CompanyName || offer.Customer?.Name || "N/A")
      .replace(/{{CustomerVAT}}/g, offer.Customer?.VATNumber || "N/A")
      .replace(/{{CustomerStreetAddress}}/g, `${offer.Customer?.Street || ""} ${offer.Customer?.Box || ""}`.trim() || "N/A")
      .replace(/{{CustomerCityCountry}}/g, [offer.Customer?.PostCode, offer.Customer?.City, offer.Customer?.Country].filter(Boolean).join(" ") || "N/A")
      .replace(/{{OfferNumber}}/g, offer.OfferNumber || '')
      .replace(/{{OfferDate}}/g, formatDate(offer.OfferDate || offer.CreatedDate))
      .replace(/{{{ProductRows}}}/g, PR)
      .replace(/{{TotalQuantity}}/g, String(tq))
      .replace(/{{{DiscountRow}}}/g, DR)
      .replace(/{{TotalExcl}}/g, (offer.BaseTotalExcl || 0).toFixed(2))
      .replace(/{{TotalVAT}}/g, (offer.TotalVAT || 0).toFixed(2))
      .replace(/{{VAT}}/g, String(offer.VATPercentage || 0))
      .replace(/{{GrandTotal}}/g, (offer.TotalIncl || offer.OfferTotal || 0).toFixed(2))
      .replace(/{{{NotesSection}}}/g, NS);
    
    // Hide VAT row if VAT percentage is 0
    if ((offer.VATPercentage || 0) === 0) {
      html = html.replace(/<div[^>]*>\s*<span>VAT[^<]*<\/span>\s*<span>[^<]*<\/span>\s*<\/div>/gi, '');
    }
    
    // Add has-content class to bank-details if IBAN exists
    if (iban && iban !== "N/A") {
      html = html.replace('<div class="bank-details">', '<div class="bank-details has-content">');
    }
    
    return html;
  };

  const handlePreviewOffer = () => {
    setPreviewHtml(getFilledTemplate());
    setOpenPreview(true);
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading offer details...
        </Typography>
      </Box>
    </Container>
  );

  if (!offer) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error">
        <AlertTitle>Not Found</AlertTitle>
        Offer not found
      </Alert>
      <Button onClick={() => navigate('/sales/offers')} sx={{ mt: 2 }}>
        Back to Offers
      </Button>
    </Container>
  );

  const statusStep = getStatusStep(offer.Status);
  const availableStatuses = getAvailableStatuses(offer.Status);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Offer #{offer.OfferNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Created: {formatDate(offer.OfferDate || offer.CreatedDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/offers')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>

            {canEditOffer(offer.Status) && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
            )}

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<PreviewIcon />}
              onClick={handlePreviewOffer}
              sx={{ borderRadius: 2 }}
            >
              Preview
            </Button>

            {/* ✅ Change Status Button */}
            {availableStatuses.length > 0 && (
              <>
                <Button
                  variant="contained"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleOpenStatusMenu}
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                    }
                  }}
                >
                  Change Status
                </Button>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor)}
                  onClose={handleCloseStatusMenu}
                  PaperProps={{
                    sx: { borderRadius: 2, minWidth: 250, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }
                  }}
                >
                  <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
                    Change Status To:
                  </Typography>
                  {availableStatuses.map((status) => (
                    <MenuItem
                      key={status.value}
                      onClick={() => handleStatusMenuClick(status.value)}
                      disabled={status.disabled}
                      sx={{ 
                        py: 1.5,
                        opacity: status.disabled ? 0.5 : 1,
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: status.color }}>
                        {status.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={status.label}
                        primaryTypographyProps={{ 
                          fontWeight: 600, 
                          color: status.color,
                          fontSize: status.disabled ? '0.85rem' : '1rem'
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Status Stepper */}
      {offer.Status !== OFFER_STATUS.REJECTED && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: '#667eea' }}>
            Offer Progress
          </Typography>
          <Stepper alternativeLabel activeStep={statusStep} connector={<ColorlibConnector />}>
            {['Draft', 'Sent', 'Accepted', 'Converted'].map((label, index) => (
              <Step key={label} completed={statusStep > index}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* Rejected Alert */}
      {offer.Status === OFFER_STATUS.REJECTED && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✗ Offer Rejected</AlertTitle>
          This offer has been rejected by the customer.
        </Alert>
      )}

      {/* Converted Alert */}
      {offer.Status === OFFER_STATUS.CONVERTED && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Converted to Sale Order</AlertTitle>
          This offer has been converted to a sale order.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Company Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{companyInfo?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{companyInfo?.vat || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{companyInfo?.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{companyInfo?.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {companyInfo?.address || 'N/A'}
                  {companyInfo?.postCode && `, ${companyInfo.postCode}`}
                  {companyInfo?.city && ` ${companyInfo.city}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">IBAN</Typography>
                <Typography variant="body1">{companyInfo?.iban || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Customer Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{offer.CustomerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{offer.CustomerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{offer.CustomerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{offer.CustomerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">{offer.CustomerAddress}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Offer Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Offer Summary
            </Typography>
            <Grid container spacing={2}>
              {/* Status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Current Status</Typography>
                <Chip
                  label={getStatusLabel(offer.Status)}
                  sx={{
                    mt: 1,
                    backgroundColor: getStatusColor(offer.Status),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 2.5,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Subtotal */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Subtotal (Excl. VAT)</Typography>
                  <Typography variant="h6" fontWeight={600}>€{offer.TotalExcl?.toFixed(2) || '0.00'}</Typography>
                </Box>
              </Grid>

              {/* Discount */}
              {offer.DiscountAmount > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#ff9800', 0.1), borderRadius: 2, border: `1px solid ${alpha('#ff9800', 0.3)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DiscountIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      <Typography variant="subtitle2" color="warning.main">Discount</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="warning.main">
                      -€{offer.DiscountAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* After Discount */}
              {offer.DiscountAmount > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">After Discount</Typography>
                    <Typography variant="h6" fontWeight={600}>€{offer.AmountAfterDiscount?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                </Grid>
              )}

              {/* VAT */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">VAT ({offer.VATPercentage || 0}%)</Typography>
                  <Typography variant="h6" fontWeight={600}>€{offer.TotalVAT?.toFixed(2) || '0.00'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Total */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: alpha('#667eea', 0.15), borderRadius: 2, border: `1px solid ${alpha('#667eea', 0.3)}` }}>
                  <Typography variant="subtitle1" fontWeight={700} color="primary">Total (Incl. VAT)</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                    €{offer.TotalIncl?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Products</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offer.LineItems?.length > 0 ? (
                    offer.LineItems.map((item, index) => {
                      const productDetails = getProductDetails(item.ProductId);
                      const productName = productDetails?.Name || item.ProductName || `Product #${item.ProductId}`;
                      const productDescription = productDetails?.Description || item.ProductDescription || '';

                      return (
                        <TableRow key={index} hover>
                          <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>{productName}</Typography>
                            {productDescription && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {productDescription}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.Quantity || 0} size="small" color="info" sx={{ fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500}>€{Number(item.UnitPrice || 0).toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                            €{Number(item.TotalExcl || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: '#888', py: 4 }}>
                        No line items found
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Totals Rows */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Subtotal (Excl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalExcl?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>

                  {offer.DiscountAmount > 0 && (
                    <TableRow sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
                      <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        Discount:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        -€{offer.DiscountAmount?.toFixed(2) || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}

                  {offer.DiscountAmount > 0 && (
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                      <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        After Discount:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                        €{offer.AmountAfterDiscount?.toFixed(2) || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      VAT ({offer.VATPercentage || 0}%):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalVAT?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total (Incl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{offer.TotalIncl?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Notes Section */}
        {(offer.CustomText || offer.Notes) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Additional Information
              </Typography>
              <Grid container spacing={3}>
                {offer.CustomText && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Custom Text</Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha('#667eea', 0.05), borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {offer.CustomText}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {offer.Notes && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal Notes</Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha('#ff9800', 0.05), borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {offer.Notes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Preview Dialog */}
      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        html={previewHtml}
        documentTitle={`offer-${offer?.OfferNumber || "draft"}`}
      />
    </Container>
  );
};

export default OfferDetailPage;